import axios from "axios";
import API_URL from "../config/api";

// ============================================
// API CONFIGURATION
// ============================================

// Use centralized API_URL from config (which reads REACT_APP_API_URL)
const RAW_API_URL = API_URL || process.env.REACT_APP_API_URL || "https://nammaeventzbackend-production.up.railway.app";

// Normalize URL (remove trailing slash)
let API_BASE = RAW_API_URL.replace(/\/$/, '');

// Add /api if not already present
if (!API_BASE.endsWith('/api')) {
  API_BASE = API_BASE + '/api';
}

// console.log(`🌐 API Base URL: ${API_BASE}`);

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true, // Send cookies with requests
  timeout: 30000, // 30 second timeout
  headers: {
    'Content-Type': 'application/json',
  }
});

// ============================================
// REQUEST INTERCEPTOR
// ============================================

api.interceptors.request.use(
  (config) => {
    // Add authorization token if available
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Add cache-busting parameter for GET requests (instead of headers)
    if (config.method === 'get' && config.url.includes('/categories')) {
      config.params = config.params || {};
      config.params._t = Date.now(); // Add timestamp to avoid caching
    }

    // Log request in development
    if (process.env.NODE_ENV === 'development') {
      console.log(`📤 ${config.method.toUpperCase()} ${config.url}`, {
        params: config.params,
        data: config.data
      });
    }

    return config;
  },
  (error) => {
    console.error('❌ Request Error:', error);
    return Promise.reject(error);
  }
);

// ============================================
// RESPONSE INTERCEPTOR
// ============================================

api.interceptors.response.use(
  (response) => {
    // Log successful response in development
    if (process.env.NODE_ENV === 'development') {
      console.log(`✅ ${response.config.method.toUpperCase()} ${response.config.url} - ${response.status}`, response.data);
    }
    return response;
  },
  (error) => {
    // 🔴 CRITICAL FIX: Graceful error handling when backend is unavailable

    // Check if this is a "backend offline" error
    const isBackendOffline =
      error.code === 'ECONNREFUSED' ||
      error.code === 'ERR_NETWORK' ||
      error.code === 'ENOTFOUND' ||
      !error.response;

    // In development, suppress excessive logging for expected failures
    if (process.env.NODE_ENV === 'development' && isBackendOffline) {
      console.warn('⚠️ Backend server is offline - feature unavailable');
      return Promise.reject({
        ...error,
        isBackendOffline: true,
        friendlyMessage: 'Backend server is not running. Please start the server.'
      });
    }

    // Enhanced error logging
    const errorDetails = {
      url: error.config?.url,
      method: error.config?.method?.toUpperCase(),
      status: error.response?.status,
      message: error.response?.data?.message || error.message,
      data: error.response?.data
    };

    if (error.response) {
      // Server responded with error status
      const { status, data } = error.response;

      switch (status) {
        case 401:
          // Unauthorized - clear auth and redirect to login
          console.warn('⚠️  Unauthorized - clearing auth data');
          localStorage.removeItem('token');
          localStorage.removeItem('user');

          // Only redirect if not already on login page
          if (!window.location.pathname.includes('/login')) {
            window.location.href = '/login';
          }
          break;

        case 403:
          // Forbidden - CORS or permission error
          console.error('🚫 Forbidden:', data?.message || 'Access denied');
          if (data?.message?.includes('CORS')) {
            console.error('💡 CORS Error - Check backend CORS configuration');
          }
          break;

        case 404:
          console.warn('⚠️  Not Found:', error.config?.url);
          break;

        case 429:
          console.warn('⚠️  Rate Limited - Too many requests');
          break;

        case 500:
        case 502:
        case 503:
          console.error('🔥 Server Error:', data?.message || 'Internal server error');
          break;

        default:
          console.error(`❌ HTTP ${status}:`, data?.message || error.message);
      }
    } else if (error.request) {
      // Request made but no response received
      if (process.env.NODE_ENV === 'development') {
        console.warn('📡 Development: Backend server is offline');
      } else {
        console.error('📡 Network Error - No response from server');
        console.error('💡 Possible causes:');
        console.error('   - Server is down or unreachable');
        console.error('   - Network connectivity issue');
        console.error('   - Request timeout');
      }
    } else {
      // Error in request setup
      console.error('⚙️  Request Setup Error:', error.message);
    }

    return Promise.reject(error);
  }
);

// ============================================
// API ENDPOINTS
// ============================================

export const workerAPI = {
  getAll: (params) => api.get("/workers", { params }),
  getById: (id) => api.get(`/workers/${id}`),
  create: (data) => api.post("/workers", data),
  update: (id, data) => api.put(`/workers/${id}`, data),
  delete: (id) => api.delete(`/workers/${id}`),
  addReview: (id, review) => api.post(`/workers/${id}/review`, review),
  approve: (id) => api.put(`/workers/${id}/approve`),
  incrementContact: (id) => api.post(`/workers/${id}/contact`),
  getBySlug: (slug) => api.get(`/workers/slug/${slug}`),
  deleteAccount: (data) => api.post('/workers/request-deletion', data),
};

export const adminAPI = {
  getDeletedAccounts: () => api.get('/workers/deleted-logs'),
  getUsers: () => api.get("/admin/users"),
  toggleUserStatus: (id) => api.put(`/admin/users/${id}/toggle-status`),
  deleteUser: (id) => api.delete(`/admin/users/${id}`),
  getAnalytics: () => api.get("/admin/analytics"),
  getMessages: () => api.get("/messages"),
  updateMessageStatus: (id, status) => api.put(`/messages/${id}`, { status }),
  deleteMessage: (id) => api.delete(`/messages/${id}`),
  setupMFA: () => api.get("/auth/mfa/setup"),
  verifyMFASetup: (token, tempSecret) => api.post("/auth/mfa/verify-setup", { token, tempSecret }),
  deleteDeletedAccount: (id) => api.delete(`/workers/deleted-logs/${id}`),
};

export const userAPI = {
  updateProfile: (data) => api.put("/user/profile", data),
  updateProfileImage: (file) => {
    const formData = new FormData();
    formData.append('profileImage', file);
    return api.put("/user/profile-image", formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  getFavorites: () => api.get("/user/favorites"),
  addFavorite: (workerId) => api.post("/user/favorites", { workerId }),
  removeFavorite: (workerId) => api.delete(`/user/favorites/${workerId}`),
  createBooking: (data) => api.post("/bookings", data),
  getBookings: () => api.get("/bookings/user"),
  getBookingById: (id) => api.get(`/bookings/${id}`),
  cancelBooking: (id) => api.put(`/bookings/${id}/cancel`, {}),
};

export const authAPI = {
  sendOTP: (email) => api.post("/auth/send-otp", { email }),
  verifyResetOTP: (email, otp) => api.post("/auth/verify-reset-otp", { email, otp }),
  verifyLoginOTP: (email, otp) => api.post("/auth/verify-otp", { email, otp }),
  resetPassword: (resetToken, newPassword) => api.post("/auth/reset-password", { resetToken, newPassword }),
  verifyMFALogin: (email, token) => api.post("/auth/mfa/login-verify", { email, token }),
  disableMFA: () => api.post("/auth/mfa/disable"),
  requestLoginOTP: (email) => api.post("/auth/login-otp/request", { email }),
  verifyOTPLogin: (email, otp) => api.post("/auth/login-otp/verify", { email, otp }),
};

export const workerRequestAPI = {
  submit: (data) => api.post("/worker-requests/submit", data),
  getAll: (params) => api.get("/worker-requests", { params }),
  updateStatus: (id, data) => api.put(`/worker-requests/${id}/status`, data),
  delete: (id) => api.delete(`/worker-requests/${id}`),
};

export const subscriptionPlanAPI = {
  getAll: () => api.get("/subscription-plans"),
  getById: (id) => api.get(`/subscription-plans/${id}`),
  create: (data) => api.post("/subscription-plans", data),
  update: (id, data) => api.put(`/subscription-plans/${id}`, data),
  delete: (id) => api.delete(`/subscription-plans/${id}`),
};

export const workerPaymentAPI = {
  createOrder: (arg1, arg2) => {
    let payload = {};
    if (typeof arg1 === 'string') {
      payload = { planType: arg1, workerRequestData: arg2 };
    } else if (arg1 && typeof arg1 === 'object') {
      payload = arg1;
    } else {
      payload = {};
    }
    return api.post("/worker/payment/create-order", payload);
  },
  verifyPayment: (data) => api.post('/worker/payment/verify', data),
  createRenewalOrder: (data) => api.post("/worker/payment/create-renewal-order", data),
  verifyRenewal: (data) => api.post("/worker/payment/verify-renewal", data),
  getHistory: () => api.get("/worker/payment/history"),
};

export const categoryAPI = {
  getAll: () => api.get("/categories"),
  getById: (id) => api.get(`/categories/${id}`),
  create: (data) => api.post("/categories", data),
  update: (id, data) => api.put(`/categories/${id}`, data),
  delete: (id) => api.delete(`/categories/${id}`),
  getAllAdmin: () => api.get("/categories/admin/all"),
  reorder: (categories) => api.post("/categories/admin/reorder", { categories }),
  bulkToggle: (categoryIds, isActive) => api.post("/categories/admin/bulk-toggle", { categoryIds, isActive }),
};

export const incomeAPI = {
  getStats: () => api.get("/admin/income/stats"),
  getAllPayments: (params) => api.get("/admin/income/payments", { params }),
  getPaymentById: (id) => api.get(`/admin/income/payments/${id}`),
  deletePayment: (id) => api.delete(`/admin/income/payments/${id}`),
};

export const activityAPI = {
  logout: () => api.post('/activity/logout'),
  getAll: (params) => api.get('/activity', { params }),
  getStats: () => api.get('/activity/stats'),
};

export const workerOnboardingAPI = {
  getPlans: async () => {
    if (process.env.NODE_ENV === 'development') {
      console.log("📡 Fetching subscription plans");
    }

    const defaultPlans = [
      { type: 'free', name: 'Free Plan (7 Days Trial)', price: 1, duration: 7, features: ['Basic Profile'] },
      { type: 'monthly', name: 'Monthly', price: 99, duration: 30, features: ['Enhanced Profile'] },
      { type: 'yearly', name: 'Yearly', price: 1099, duration: 365, features: ['Premium Profile'] },
    ];

    try {
      const response = await api.get('/worker/payment/plans');
      if (process.env.NODE_ENV === 'development') {
        console.log('✅ Plans fetched successfully');
      }
      return response;
    } catch (err) {
      console.warn('⚠️  Primary plans endpoint failed, trying fallback');
      try {
        const fallback = await api.get('/subscription-plans');
        console.log('✅ Fallback plans fetched successfully');
        return fallback;
      } catch (err2) {
        console.warn('⚠️  All endpoints failed, using default plans');
        return Promise.resolve({ data: { data: defaultPlans } });
      }
    }
  },
  createOrder: (planType, workerRequestData) => {
    console.log("📡 Creating order for plan:", planType);
    return api.post("/worker/payment/create-order", { planType, workerRequestData });
  },
  subscribeFree: (workerRequestData) => {
    console.log("📡 Subscribing to free plan");
    return api.post("/worker/payment/create-order", { planType: 'free', workerRequestData });
  },
  verifyPayment: (data) => {
    if (process.env.NODE_ENV === 'development') {
      console.log("📡 Verifying payment");
    }
    return api.post("/worker/payment/verify", data);
  },
  getMySubscription: () => {
    if (process.env.NODE_ENV === 'development') {
      console.log("📡 Getting subscription status");
    }
    return api.get("/worker/subscription-status");
  },
};

// ============================================
// UTILITY FUNCTIONS
// ============================================

// Test API connection
export const testConnection = async () => {
  try {
    const response = await api.get('/health');
    if (process.env.NODE_ENV === 'development') {
      console.log('✅ API Connection Test:', response.data);
    }
    return true;
  } catch (error) {
    console.error('❌ API Connection Test Failed:', error.message);
    return false;
  }
};

export default api;
